/**
 * A Integridade e o Vínculo de uma linha da custódia pelo painel (E211.F2, owner, 01/11; ADR-0146,
 * adendo E211.F2): a linha do documento na custódia (PARTE, seção 2) ou na integridade (JUÍZO,
 * seção 4) abre com os dois resultados impressos; o perito muda um ou os dois com um fundamento só,
 * que sai no exame como nota numerada na célula revista. Os eixos de integridade e de vínculo da
 * página 1 são recalculados; o hash declarado, o SHA-256 de custódia e o carimbo não mudam.
 *
 * Cada coluna que mudou grava a própria revisão (`custodia:<letra>:<coluna>`), com o mesmo
 * fundamento (a mesma nota). A palavra impressa que não é um resultado do perito (NÃO RECEBIDO,
 * PRESERVADA) abre sem escolha; o Salvar fica ligado e diz o que falta (E211.F1.T05).
 */
import { useState } from 'react';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  COLUNAS_DA_CUSTODIA,
  RESULTADOS_DA_LINHA,
  chaveDaCustodia,
  resultadoImpresso,
  vigenteEntre,
  type ColunaDaCustodia,
  type ResultadoDaLinha,
} from '@/lib/revisaoDoDado';

import { Fundamento, faltaNoFundamento } from './Fundamento';
import { BotaoSalvar, Falta, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.custodia;
const falta = ptBR.painelDoDado.falta;

type Escolhas = Record<ColunaDaCustodia, ResultadoDaLinha | ''>;

export function CustodiaNoPainel(props: PropsDoConteudo) {
  const { pedido, acoes, onMudou } = props;
  const linha = pedido.origem.linha ?? '';
  const [impressos] = useState<Escolhas>(() => ({
    integridade: resultadoImpresso('integridade', pedido.origem.valor),
    vinculo: resultadoImpresso('vinculo', pedido.origem.texto),
  }));
  const [escolhas, setEscolhas] = useState<Escolhas>(impressos);
  const [fundamento, setFundamento] = useState('');
  const [oQueFalta, setOQueFalta] = useState<string | null>(null);
  const mudadas = COLUNAS_DA_CUSTODIA.filter(
    (c) => escolhas[c] !== '' && escolhas[c] !== impressos[c],
  );
  const gravacao = useGravacao(
    async () => {
      for (const coluna of mudadas) {
        const recusa = await gravarRevisao(props, {
          alvo: 'linha',
          chave: chaveDaCustodia(linha, coluna),
          valor: escolhas[coluna],
          fundamento: fundamento.trim(),
        });
        if (recusa !== null) return recusa;
      }
      return null;
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  function salvar(): void {
    const pendente = mudadas.length === 0 ? falta.custodia : faltaNoFundamento(fundamento);
    setOQueFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }
  return (
    <div className="space-y-sc-3" data-testid="painel-custodia">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      {COLUNAS_DA_CUSTODIA.map((coluna) => (
        <Field key={coluna} label={t.colunas[coluna]} htmlFor={`painel-custodia-${coluna}`}>
          <Select
            id={`painel-custodia-${coluna}`}
            value={escolhas[coluna]}
            onChange={(e) =>
              setEscolhas((atual) => ({
                ...atual,
                [coluna]: vigenteEntre(RESULTADOS_DA_LINHA, e.target.value),
              }))
            }
            data-testid={`painel-custodia-${coluna}`}
          >
            <option value="" disabled>
              {t.escolha}
            </option>
            {RESULTADOS_DA_LINHA.map((r) => (
              <option key={r} value={r}>
                {t.resultados[coluna][r]}
              </option>
            ))}
          </Select>
        </Field>
      ))}
      <Fundamento id="painel-custodia-fundamento" valor={fundamento} onChange={setFundamento} />
      <Falta falta={oQueFalta} estado={gravacao.estado} testId="painel-custodia-falta" />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-custodia-salvar"
      />
    </div>
  );
}
